import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class ResetClientLogoRPC implements
  LeoRPC<ResetClientLogoRPC.Request, ResetClientLogoRPC.Response, ResetClientLogoRPC.Errors.Errors>
{
  abstract execute(
    request: ResetClientLogoRPC.Request
  ): Promise<LeoRPCResult<ResetClientLogoRPC.Response, ResetClientLogoRPC.Errors.Errors>>;
}

export namespace ResetClientLogoRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace LogoType {
      export enum LogoType {
        DARK_THEME = "DARK_THEME",
        LIGHT_THEME = "LIGHT_THEME",
      }
    
      export interface LogoTypeDTO {
        case: string;
      }
    
      export const fromDTO = (dto: LogoTypeDTO): LogoType => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestLogoTypeError(
            `Invalid DTO ${dto} provided for LogoType.LogoType`
          );
        }
        switch (dto.case) {
          case "DARK_THEME":
            return LogoType.DARK_THEME;
          case "LIGHT_THEME":
            return LogoType.LIGHT_THEME;
          default:
            throw new InvalidRequestLogoTypeError(
              `Case ${dto.case} is not valid case of LogoType.LogoType`
            );
        }
      };
    
      export const toDTO = (logoType: LogoType): LogoTypeDTO => {
        const enumDTO = {
          case: LogoType[logoType],
        };
        return enumDTO;
      };
    }
    
  }
  export interface RequestDTO {
    surveyId: string;
    projectId: string;
    logoType: RequestEnums.LogoType.LogoTypeDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isResetClientLogoRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
    readonly logoType: RequestEnums.LogoType.LogoType;
  
  
    constructor(
    surveyId: LeoUUID, 
    projectId: LeoUUID, 
    logoType: RequestEnums.LogoType.LogoType
    ) {
      this.surveyId = surveyId;
      this.projectId = projectId;
      this.logoType = logoType;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const logoType = RequestEnums.LogoType.fromDTO(getObject(dto, "logoType", InvalidRequestLogoTypeError));
      return new Request(
        surveyId,
        projectId,
        logoType
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO(),
          logoType: RequestEnums.LogoType.toDTO(this.logoType)
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ResetClientLogoRPC(surveyId='${this.surveyId}',projectId='${this.projectId}',logoType='${this.logoType}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    imageURL: string;
    imageDocumentId: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isResetClientLogoResponse: boolean = true;
  
    readonly imageURL: URL;
    readonly imageDocumentId: LeoUUID;
  
  
    constructor(
    imageURL: URL, 
    imageDocumentId: LeoUUID
    ) {
      this.imageURL = imageURL;
      this.imageDocumentId = imageDocumentId;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const imageURL = getUrl(dto, "imageURL", InvalidResponseImageUrlError);
      const imageDocumentId = getLeoUuid(dto, "imageDocumentId", InvalidResponseImageDocumentIdError);
      return new Response(
        imageURL,
        imageDocumentId
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          imageURL: this.imageURL.toString(),
          imageDocumentId: this.imageDocumentId.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `ResetClientLogo(imageURL='${this.imageURL}',imageDocumentId='${this.imageDocumentId}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `ResetClientLogoRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ResetClientLogoRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ResetClientLogoRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | ProjectAlreadyArchived;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends ResetClientLogoRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends ResetClientLogoRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestLogoTypeError extends ResetClientLogoRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLogoTypeError";
  }
}

export class InvalidRequestLogoTypeDarkThemeError extends InvalidRequestLogoTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLogoTypeDarkThemeError";
  }
}

export class InvalidRequestLogoTypeLightThemeError extends InvalidRequestLogoTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestLogoTypeLightThemeError";
  }
}

export class InvalidResponseImageUrlError extends ResetClientLogoRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseImageUrlError";
  }
}

export class InvalidResponseImageDocumentIdError extends ResetClientLogoRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseImageDocumentIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends ResetClientLogoRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends ResetClientLogoRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ResetClientLogoRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

